import { motion } from "framer-motion";
import { LinkedinLogo, EnvelopeSimple, Quotes } from "@phosphor-icons/react";
import SEO from "@/components/site/SEO";
import LegalLayout from "@/components/site/LegalLayout";

const leaders = [
  {
    role: "Founder",
    name: "[ Founder Name ]",
    title: "Founder & Chairman",
    bio: "With extensive experience in international trade and cross-border commerce, our Founder established Altin Link with a vision to bridge manufacturers, suppliers and investors across Türkiye, Europe and the Middle East. Their leadership is grounded in integrity, discretion and a deep network of trusted commercial relationships.",
    initials: "AL",
  },
  {
    role: "Managing Director",
    name: "[ Managing Director Name ]",
    title: "Managing Director",
    bio: "Our Managing Director oversees the firm's day-to-day operations and client engagements, ensuring every mandate is executed to the highest professional standard. With a background in commercial consulting and international business development, they lead our matchmaking, negotiation and partnership programmes.",
    initials: "MD",
  },
];

export default function Leadership() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "Leadership | Altin Link",
    url: "https://www.altin-link.com/leadership",
  };

  return (
    <>
      <SEO
        title="Leadership"
        description="Meet the leadership of Altin Link Danışmanlık Hizmetleri Ltd. Şti. — our Founder and Managing Director, our company vision and our leadership philosophy."
        path="/leadership"
        schema={schema}
      />
      <LegalLayout
        testid="leadership-page"
        overline="Leadership"
        title="Our"
        accent="Leadership"
        intro="The people and principles behind Altin Link — experienced professionals dedicated to building trusted international commercial partnerships."
      >
        {/* Leaders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {leaders.map((l, i) => (
            <motion.div
              key={l.role}
              data-testid={`leader-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="bg-white border border-altin-border p-8 md:p-10"
            >
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 grid place-items-center bg-altin-ink text-altin-gold font-serif-display text-3xl shrink-0">
                  {l.initials}
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-altin-gold">
                    {l.role}
                  </div>
                  <h2 className="font-serif-display text-2xl text-altin-ink tracking-tight mt-1">
                    {l.name}
                  </h2>
                  <div className="text-sm text-altin-muted">{l.title}</div>
                </div>
              </div>
              <p className="mt-6 text-altin-muted leading-relaxed">{l.bio}</p>
              <div className="mt-6 flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/altin-link"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="w-9 h-9 grid place-items-center border border-altin-border text-altin-muted hover:border-altin-gold hover:text-altin-gold transition-colors"
                >
                  <LinkedinLogo size={16} weight="light" />
                </a>
                <a
                  href="mailto:info@altin-link.com"
                  aria-label="Email"
                  className="w-9 h-9 grid place-items-center border border-altin-border text-altin-muted hover:border-altin-gold hover:text-altin-gold transition-colors"
                >
                  <EnvelopeSimple size={16} weight="light" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Vision */}
        <motion.div
          data-testid="leadership-vision"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-14 bg-altin-ink text-white p-10 md:p-14 relative overflow-hidden"
        >
          <Quotes
            size={64}
            weight="fill"
            className="text-altin-gold/20 absolute top-6 right-8"
          />
          <div className="text-xs tracking-[0.25em] uppercase text-altin-gold mb-4">
            Company Vision
          </div>
          <p className="font-serif-display text-2xl md:text-3xl leading-snug tracking-tight max-w-3xl">
            To become a leading international business bridge connecting
            opportunities between Türkiye, Europe, the Middle East and emerging
            global markets — built on trust, discretion and measurable value.
          </p>
        </motion.div>

        {/* Philosophy */}
        <motion.div
          data-testid="leadership-philosophy"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-10"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-altin-gold mb-3">
            Leadership Philosophy
          </div>
          <h2 className="font-serif-display text-3xl text-altin-ink tracking-tight">
            Leading by relationship, not transaction.
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 text-altin-muted leading-relaxed">
            <p>
              Our leadership believes that lasting commercial success is built
              on trust earned over time. We prioritise the long-term interests
              of our clients and partners over short-term gain, and we hold
              ourselves to the highest standards of confidentiality and
              professional conduct.
            </p>
            <p>
              We lead with a global mindset and local sensitivity — combining
              deep market knowledge with cultural fluency to navigate complex
              cross-border relationships. Every member of our team is empowered
              to act with integrity and accountability on behalf of those we
              represent.
            </p>
          </div>
        </motion.div>
      </LegalLayout>
    </>
  );
}
